import React from 'react';
import { Box, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';

interface QuestionProgressControlsProps {
  totalQuestions: number;
  answeredFlags: boolean[];
  currentQuestionIndex: number;
  viewMode: 'all' | 'single';
  onViewModeChange: (mode: 'all' | 'single') => void;
  onPrevious: () => void;
  onNext: () => void;
}

const QuestionProgressControls: React.FC<QuestionProgressControlsProps> = ({
  totalQuestions,
  answeredFlags,
  currentQuestionIndex,
  viewMode,
  onViewModeChange,
  onPrevious,
  onNext,
}) => {
  if (!totalQuestions) {
    return null;
  }

  const answeredQuestionCount = answeredFlags.filter(Boolean).length;
  const progressValue = viewMode === 'single' && totalQuestions > 1
    ? ((currentQuestionIndex + 1) / totalQuestions) * 100
    : (answeredQuestionCount / totalQuestions) * 100;

  return (
    <Box sx={{ mb: 3 }}>
      {totalQuestions > 1 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap sx={{ mb: 2 }}>
          <Button
            variant={viewMode === 'single' ? 'contained' : 'outlined'}
            onClick={() => onViewModeChange('single')}
          >
            Vastaa yksi kerrallaan
          </Button>
          <Button
            variant={viewMode === 'all' ? 'contained' : 'outlined'}
            onClick={() => onViewModeChange('all')}
          >
            Näytä kaikki kysymykset
          </Button>
        </Stack>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="subtitle2">
            {viewMode === 'single' && totalQuestions > 1
              ? `Kysymys ${currentQuestionIndex + 1} / ${totalQuestions}`
              : `Kaikki kysymykset näkyvissä (${totalQuestions})`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {answeredQuestionCount} / {totalQuestions} vastattu
          </Typography>
        </Box>

        <LinearProgress variant="determinate" value={progressValue} sx={{ height: 8, borderRadius: 999 }} />

        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${totalQuestions}, minmax(0, 1fr))`, gap: 0.75, mt: 1.25 }}>
          {answeredFlags.map((isAnswered, index) => (
            <Box
              key={`question-progress-${index + 1}`}
              sx={{
                height: 8,
                borderRadius: 999,
                backgroundColor: viewMode === 'single' && index === currentQuestionIndex
                  ? 'primary.main'
                  : isAnswered
                    ? 'success.light'
                    : viewMode === 'single' && index < currentQuestionIndex
                      ? 'primary.light'
                      : 'divider',
              }}
            />
          ))}
        </Box>
      </Paper>

      {viewMode === 'single' && totalQuestions > 1 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            onClick={onPrevious}
            disabled={currentQuestionIndex === 0}
          >
            Edellinen kysymys
          </Button>
          <Button
            variant="contained"
            onClick={onNext}
            disabled={currentQuestionIndex === totalQuestions - 1}
          >
            Seuraava kysymys
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default QuestionProgressControls;